/**
 * Meta Pixel Tracking Utility
 * Handles all Facebook Pixel events with proper data formatting
 */

// Check if Meta Pixel is available and enabled
export const isMetaPixelAvailable = () => {
  return (
    typeof window !== 'undefined' &&
    window.fbq &&
    process.env.REACT_APP_META_PIXEL_ENABLED === 'true'
  );
};

// Safe wrapper for standard events
export const trackMetaEvent = (eventName, eventData = {}) => {
  if (!isMetaPixelAvailable()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Meta Pixel - Dev]', eventName, eventData);
    }
    return;
  }

  try {
    window.fbq('track', eventName, eventData);

    if (process.env.REACT_APP_DEBUG_PIXEL === 'true') {
      console.log('[Meta Pixel]', eventName, eventData);
    }
  } catch (error) {
    console.error('[Meta Pixel Error]', error);
  }
};

// Custom event tracking
export const trackCustomEvent = (eventName, eventData = {}) => {
  if (!isMetaPixelAvailable()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Meta Pixel Custom - Dev]', eventName, eventData);
    }
    return;
  }

  try {
    window.fbq('trackCustom', eventName, eventData);

    if (process.env.REACT_APP_DEBUG_PIXEL === 'true') {
      console.log('[Meta Pixel Custom]', eventName, eventData);
    }
  } catch (error) {
    console.error('[Meta Pixel Custom Error]', error);
  }
};

// Currency normalizer - handles Rs. vs LKR inconsistencies
export const normalizeCurrency = (currency) => {
  if (!currency) return 'LKR';

  const normalized = currency.toString().toUpperCase().trim();

  if (normalized.includes('RS') || normalized.includes('LKR')) {
    return 'LKR';
  }

  return 'LKR'; // Default
};

// Price parser - handles string prices with currency symbols
export const parsePrice = (priceInput) => {
  if (typeof priceInput === 'number') {
    return priceInput;
  }

  if (typeof priceInput === 'string') {
    // Remove all non-numeric except decimal point
    return parseFloat(priceInput.replace(/[^\d.]/g, '')) || 0;
  }

  return 0;
};

// Format product data for ViewContent
export const formatProductData = (product, quantity = 1) => {
  return {
    content_ids: [product.id],
    content_name: product.name,
    content_type: 'product',
    value: parsePrice(product.price) * quantity,
    currency: normalizeCurrency(product.priceCurrency),
  };
};

// Format cart item data for AddToCart
export const formatCartItemData = (item) => {
  return {
    content_ids: [item.id],
    content_name: item.name,
    content_type: 'product',
    value: parsePrice(item.price),
    currency: 'LKR',
  };
};

// Format cart data for InitiateCheckout
export const formatCartData = (cartItems) => {
  const content_ids = cartItems.map((item) => item.id);
  const contents = cartItems.map((item) => ({
    id: item.id,
    quantity: item.quantity,
    item_price: parsePrice(item.price),
  }));

  const total = cartItems.reduce(
    (sum, item) => sum + parsePrice(item.price) * item.quantity,
    0
  );

  return {
    content_ids,
    contents,
    content_type: 'product',
    value: total,
    currency: 'LKR',
    num_items: cartItems.reduce((sum, item) => sum + item.quantity, 0),
  };
};

// Calculate order value with all discounts and delivery
export const calculateOrderValue = (order) => {
  const pricing = order.priceAtOrder || order.pricing || {};

  // Priority 1: Final amount (already calculated with all discounts)
  if (pricing.finalAmount !== undefined && pricing.finalAmount !== null) {
    return parseFloat(pricing.finalAmount);
  }

  // Priority 2: Total from pricing object
  if (pricing.total) {
    return parseFloat(pricing.total);
  }

  // Priority 3: Subtotal
  if (pricing.subtotal) {
    return parseFloat(pricing.subtotal);
  }

  // Priority 4: Calculate from components
  const quantity = pricing.quantity || 1;
  let unitPrice = pricing.unitPrice || pricing.amount || 0;

  // Handle size/variant pricing
  if (pricing.size?.priceDelta) {
    unitPrice = pricing.size.priceDelta;
  }

  let total = unitPrice * quantity;



  // Add delivery if not included
  if (!pricing.deliveryIncluded && pricing.deliveryPrice) {
    total += pricing.deliveryPrice;
  }

  return Math.max(0, total);
};

// Format order data for Purchase event
export const formatPurchaseData = (order) => {
  const pricing = order.priceAtOrder || order.pricing || {};
  const items = order.items || [];

  // For cart-based orders
  if (items.length > 0) {
    return {
      content_ids: items.map((item) => item.id),
      contents: items.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        item_price: parsePrice(item.price),
      })),
      content_type: 'product',
      value: calculateOrderValue(order),
      currency: 'LKR',
      num_items: items.reduce((sum, item) => sum + item.quantity, 0),
    };
  }

  // For direct product orders (ProductPage)
  const quantity = pricing.quantity || 1;
  return {
    content_ids: [order.productId],
    content_name: order.productName,
    content_type: 'product',
    contents: [
      {
        id: order.productId,
        quantity: quantity,
        item_price: pricing.unitPrice || pricing.amount,
      },
    ],
    value: calculateOrderValue(order),
    currency: normalizeCurrency(pricing.currency),
    num_items: quantity,
  };
};
